/**
 * Employee workspace information architecture (DIGEMP-EMPLOYEE-WORKSPACE-V2-FRONTEND-075).
 *
 * One registry owns the reading order of the coworker workspace, so the jump navigation and the
 * rendered sections can never drift apart. The section ids are stable anchors only — they add no
 * route and no permission of their own; every section body below still renders exactly what the
 * existing data contracts allow.
 *
 * DIGEMP-CLOSEOUT-WORKSPACE-A11Y-211: the jump now moves keyboard focus to every real section (the
 * page's sections head with h3 or with no heading at all, so heading-only focus silently dropped
 * the listener back on the nav), the scroll honors prefers-reduced-motion, and the bar declares
 * RTL like the sibling Hebrew surfaces.
 */
import { prefersReducedMotionNow } from "./useLiveState";

export const EMPLOYEE_WORKSPACE_SECTIONS = [
  {
    id: "employee-work",
    label: "עבודה",
    description: "מה העובד עושה כעת — עבודה פעילה, תור המשימות ומה דורש טיפול",
  },
  {
    id: "employee-conversations",
    label: "שיחות",
    description: "שיחות עם העובד, מה ממתין למי, ומעבר לשיחה המלאה",
  },
  {
    id: "employee-capabilities",
    label: "יכולות",
    description: "מה העובד רשאי להפעיל — מערכות, כלים ומעטפת ההרשאות",
  },
  {
    id: "employee-skills",
    label: "מיומנויות",
    description: "הערכה מדידה מתוך ריצות שהסתיימו",
  },
  {
    id: "employee-growth",
    label: "התפתחות",
    description: "התמחות מוצהרת מול ראיות נמדדות, ותקציר קורות חיים מקצועי",
  },
  {
    id: "employee-learning",
    label: "למידה",
    description: "פערי ידע שהעובד חשף בעבודתו — בסיס ללמידה הבאה",
  },
  {
    id: "employee-systems",
    label: "מערכות",
    description: "חיבורים, בדיקות חיבור ובדיקות מתוזמנות",
  },
  {
    id: "employee-evidence",
    label: "תיעוד ועדויות",
    description: "ריצות שהסתיימו, תוצאות מאומתות ויומן הפעילות",
  },
];

export function jumpToSection(id) {
  const section = typeof document === "undefined" ? null : document.getElementById(id);
  if (!section) return;
  if (typeof section.scrollIntoView === "function") {
    // Smooth scrolling is a motion effect — honor the OS-level preference.
    section.scrollIntoView({
      behavior: prefersReducedMotionNow() ? "auto" : "smooth",
      block: "start",
    });
  }
  // Move keyboard focus with the visual jump so tabbing continues from the section the
  // operator just navigated to, instead of from the navigation they left. The workspace's
  // own sections head with h3 (or with no heading at all), so fall back to the section
  // itself rather than leaving focus behind on the nav link.
  const heading = section.querySelector("h1, h2, h3, h4, h5, h6");
  const target = heading || section;
  target.setAttribute("tabindex", "-1");
  target.focus({ preventScroll: true });
}

export function WorkspaceNav({ sections = EMPLOYEE_WORKSPACE_SECTIONS }) {
  // No destinations (e.g. a permission-filtered list that came back empty) means no
  // navigation — an empty labeled landmark would be worse than none.
  if (!sections || sections.length === 0) return null;
  return (
    <nav
      dir="rtl"
      data-testid="employee-workspace-nav"
      aria-label="ניווט בסביבת העובד"
      className="bg-white rounded-xl border shadow-sm px-3 py-2 flex flex-wrap items-center gap-1.5 sticky top-2 z-10"
    >
      <span className="text-xs font-semibold text-gray-500 ml-2">סביבת העובד</span>
      {sections.map((section) => (
        <a
          key={section.id}
          href={`#${section.id}`}
          onClick={(event) => {
            event.preventDefault();
            jumpToSection(section.id);
          }}
          className="text-xs px-2 py-1 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 whitespace-nowrap"
        >
          {section.label}
        </a>
      ))}
    </nav>
  );
}

export function WorkspaceSection({ id, title, description, count, children }) {
  return (
    <section id={id} aria-label={title} className="space-y-3 scroll-mt-24">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500">{description}</p>}
        {count != null && <span className="text-xs text-gray-400 mr-auto">{count}</span>}
      </div>
      {children}
    </section>
  );
}

export default WorkspaceNav;
